/* ==========================================================================
   LUJE — keep the free-shipping progress bar in sync with the cart.

   The problem it solves
   ---------------------
   `luje-cart-goal.liquid` renders inside `.scd__header`. When the cart changes
   the theme's own script (app.min.js) re-renders `.scd__items`,
   `.scd__summary`, `.scd__footer` and `.scd__addons` — but never
   `.scd__header`. So the bar kept whatever value the server rendered at page
   load: add £129.95 of product and it still said "You are €149,95 away",
   stuck at 0%, until you reloaded. Reported by Riccardo 2026-08-08.

   Why it re-fetches instead of recalculating
   ------------------------------------------
   The threshold, both messages and the money formatting all live in Liquid and
   in theme settings. Recomputing them here would mean a second copy of the
   arithmetic, a second money formatter, and a hardcoded threshold that would
   silently disagree with the setting the moment a merchant changed it. So this
   asks the Section Rendering API for a freshly rendered drawer and lifts the
   already-correct bar out of it. One source of truth stays in Liquid.

   It only ever enhances: with JavaScript off, the server-rendered bar is still
   correct on page load. Nothing here gates content.
   ========================================================================== */
(function () {
  'use strict';

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var inFlight = false;
  var queued = false;
  var timer = null;

  // Everything in .scd__header goes stale for the same reason, not just the
  // progress bar — the item-count badge beside the title had the same problem.
  // So the whole header is refreshed. On the cart PAGE there is no .scd__header,
  // only a standalone bar, hence the second selector (filtered so a bar inside a
  // header is not handled twice).
  function targets() {
    var out = Array.prototype.slice.call(document.querySelectorAll('.scd__header'));
    document.querySelectorAll('.luje-cart-goal').forEach(function (bar) {
      if (!bar.closest('.scd__header')) out.push(bar);
    });
    return out;
  }

  function refresh() {
    if (inFlight) { queued = true; return; }
    if (!targets().length) return;   // nothing on this page to update
    inFlight = true;

    fetch(root + '?section_id=cart-drawer', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : Promise.reject(r.status); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        targets().forEach(function (node) {
          var sel = node.classList.contains('scd__header') ? '.scd__header' : '.luje-cart-goal';
          var fresh = doc.querySelector(sel);
          if (fresh) {
            // Identical content means the cart did not change: skip the write.
            // Without this guard the innerHTML assignment below emits mutation
            // records even for identical HTML, and any observer that sees them
            // schedules another refresh — this ran as an infinite fetch loop
            // (one section render every ~0.6s, forever) until 24 Aug 2026.
            if (fresh.innerHTML === node.innerHTML &&
                (fresh.getAttribute('style') || '') === (node.getAttribute('style') || '')) {
              return;
            }
            node.setAttribute('style', fresh.getAttribute('style') || '');
            node.innerHTML = fresh.innerHTML;
          } else if (sel === '.luje-cart-goal') {
            // The bar is currency-guarded in Liquid (EUR only). If a fresh
            // render omits it, it should not linger on screen.
            node.remove();
          }
        });
      })
      .catch(function () { /* leave the last good value on screen */ })
      .then(function () {
        inFlight = false;
        if (queued) { queued = false; schedule(); }
      });
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(refresh, 150);   // coalesce bursts of DOM writes
  }

  /* ---- triggers -------------------------------------------------------- */

  // The nodes the theme DOES re-render on a cart change. Watching them is how
  // we learn the cart moved without having to patch or depend on app.min.js —
  // it dispatches no cart event of its own.
  function watch(node) {
    if (!node || node.__lujeGoalWatched) return;
    node.__lujeGoalWatched = true;
    new MutationObserver(schedule).observe(node, {
      childList: true, subtree: true, characterData: true
    });
  }

  function attach() {
    ['.scd__items', '[data-cart-subtotal]', '.scd__body'].forEach(function (s) {
      document.querySelectorAll(s).forEach(watch);
    });
  }

  // The drawer is injected into #cart-drawer-container after load, so watch the
  // container for the drawer arriving, then attach to its inner regions.
  // subtree stays true because app.min.js replaces .scd__body wholesale on cart
  // changes and attach() must re-watch the new node. But refresh() itself
  // writes into .scd__header / .luje-cart-goal INSIDE this container, and
  // reacting to our own writes turned this observer into an infinite
  // fetch loop (found in the 23 Aug 2026 performance audit). Mutations that
  // land entirely inside our own write targets are therefore ignored.
  var container = document.getElementById('cart-drawer-container');
  if (container) {
    new MutationObserver(function (muts) {
      var own = muts.every(function (m) {
        var t = m.target.nodeType === 1 ? m.target : m.target.parentElement;
        return t && t.closest && t.closest('.scd__header, .luje-cart-goal');
      });
      if (own) return;
      attach();
      schedule();
    }).observe(container, { childList: true, subtree: true });
  }

  document.addEventListener('DOMContentLoaded', attach);
  attach();

  // Opening the drawer is also a good moment to re-sync: the cart may have
  // changed in another tab, or on a page we navigated back to from bfcache.
  document.addEventListener('click', function (e) {
    var t = e.target.closest && e.target.closest('a[href$="/cart"], .scd__toggle, [data-cart-drawer-toggle]');
    if (t) schedule();
  }, true);

  window.addEventListener('pageshow', function (e) { if (e.persisted) schedule(); });
})();
